import {ProgressAria} from '_components/ProgressAria';
import LFCSingleValues from '_components/surfaces/LFCSingleValues';
import {fmtYMD} from '_logics/LFCFormatUtil';
import {rendTotalJudgeDisp} from '_logics/LFCRenderUtil';
import {adminAPIAccess, getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import DownloadIcon from '@mui/icons-material/Download';
import {Button, Grid, Stack, Tooltip} from '@mui/material';
import ExcelJS from 'exceljs';

/**
 * 引数
 */
interface Props {
  uuid: string;
}

export const HeaderPanel = ({uuid}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);

  const [dsTop, setDsTop] = useState({});
  const [serial, setSerial] = useState('');

  const onDownloadCSV = () => {
    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    document.body.style.cursor = 'wait';

    cancelSource.current = axios.CancelToken.source();
    adminAPIAccess({
      apipath: 'GetInspectionResult',
      parameters: {
        serialno: serial,
        uuid: uuid
      }
    })
      .then(response => {
        let bom = new Uint8Array([0xef, 0xbb, 0xbf]);
        const blob = new Blob([bom, response.data], {type: 'text/plain'});
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${t('シリアルデータ')}_${serial}.csv`;
        a.click();
        a.remove();
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setStartProcess(false);
        document.body.style.cursor = 'auto';
      });
  };

  const onDownloadExcel = () => {
    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    document.body.style.cursor = 'wait';

    cancelSource.current = axios.CancelToken.source();
    adminAPIAccess({
      apipath: 'GetInspectionResult',
      parameters: {
        serialno: serial,
        uuid: uuid
      }
    })
      .then(async response => {
        const data: string[][] = response.data.split('\r\n').map((row: any) => row.split(','));
        const workbook = new ExcelJS.Workbook();
        workbook.addWorksheet('export_data');
        const worksheet = workbook.getWorksheet('export_data');
        data.forEach((row, indexRow) => {
          if (indexRow === 0) {
            const header_data: {header: string; key: string}[] = [];
            row.forEach((cell, indexCol) => {
              header_data.push({header: cell.slice(1).slice(0, -1), key: `c${indexCol}`});
            });
            worksheet.columns = header_data;
          } else {
            let row_data = {};
            row.forEach((cell, indexCol) => {
              if (cell.length > 0) {
                row_data = {...row_data, [`c${indexCol}`]: cell.slice(1).slice(0, -1)};
              }
            });
            worksheet.addRow(row_data);
          }
        });
        const uint8Array = await workbook.xlsx.writeBuffer();
        const blob = new Blob([uint8Array], {type: 'application/octet-binary'});
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${t('シリアルデータ')}_${serial}.xlsx`;
        a.click();
        a.remove();
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setStartProcess(false);
        document.body.style.cursor = 'auto';
      });
  };

  /**
   * 検索処理
   */
  useEffect(() => {
    if (uuid === '') return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      //検査結果_詳細単行
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 69,
        parameters: {
          uuid: uuid
        },
        name: `${t('検査結果_詳細単行')}`,
        cancelToken: cancelSource.current.token,
        t
      }).then((datas: any) => {
        if (datas.length > 0) {
          setDsTop(datas[0]);
          setSerial(datas[0]['serial']);
        }
      })
    ]).finally(() => {
      setStartProcess(false);
    });
  }, [uuid]);

  useEffect(() => {
    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  return (
    <ProgressAria startProcess={startProcess}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={10}>
          <LFCSingleValues
            columns={[
              {field: 'judge', headerName: `${t('総合判定')}`, formatter: rendTotalJudgeDisp},
              {field: 'lq_time', headerName: t('検査日時'), formatter: fmtYMD},
              {field: 'serial', headerName: `${t('シリアルNo')}`},
              {field: 'work', headerName: `${t('機種')}`},
              {field: 'deviceid', headerName: t('検査装置')},
              {field: 'dongleid', headerName: t('オペレータ')},
              {field: 'lv2count', headerName: t('検査回数')}
            ]}
            source={dsTop}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title={t('CSV出力')} placement="bottom" arrow={true}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={onDownloadCSV}
              >
                CSV
              </Button>
            </Tooltip>
            <Tooltip title={t('Excel出力')} placement="bottom" arrow={true}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={onDownloadExcel}
              >
                Excel
              </Button>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
    </ProgressAria>
  );
};
